import { useQuery } from '@tanstack/react-query';

import ExternalApiService, {
  type UsersProps,
} from '../services/ExternalService/ExternalApiService';
import { type UsersResponse } from '../services/types';

/**
 * Fetches user data from the external API service based on the provided user properties.
 *
 * @async
 * @function fetchUsers
 * @param {UsersProps} userProps - The user properties required to fetch user data.
 * @returns {Promise<UsersResponse>} A promise that resolves to the user data response.
 */
const fetchUsers = async (userProps: UsersProps): Promise<UsersResponse> => {
  const response = (await ExternalApiService.postUsers(
    userProps
  )) as UsersResponse;

  return response;
};

/**
 * Custom React hook to fetch and cache user data from the external API using React Query.
 *
 * @function useGetUsers
 * @param {UsersProps} userProps - The user properties required for fetching user data.
 * @returns {import('@tanstack/react-query').UseQueryResult<UsersResponse, unknown>} An object containing the query result, including the user data, loading state, and potential errors.
 *
 */
const useGetUsers = (userProps: UsersProps) =>
  useQuery<UsersResponse>({
    queryKey: ['DASHBOARD_USERS', userProps], // Unique query key
    queryFn: () => fetchUsers(userProps), // Function to fetch data
    refetchOnWindowFocus: false,
    enabled: userProps.enabled,
  });

export default useGetUsers;
